<template>
  <div class="page">
    <div v-if="loading" class="state">加载中…</div>
    <div v-else-if="error" class="state state--error">
      <p>{{ error }}</p>
    </div>
    <template v-else-if="submitted">
      <div class="success-card">
        <div class="success-card__icon">✓</div>
        <h1>提交成功</h1>
        <p>{{ successMsg }}</p>
        <p class="success-card__sub">非已承保，业务人员将联系您完善方案</p>
      </div>
    </template>
    <template v-else-if="detail">
      <!-- 顶部轮播 / 主图 -->
      <section class="banner" v-if="bannerList.length > 0">
        <div
          class="banner__track"
          :style="{ transform: `translateX(-${bannerIndex * 100}%)` }"
          @touchstart.passive="onTouchStart"
          @touchend.passive="onTouchEnd"
        >
          <div class="banner__slide" v-for="(url, idx) in bannerList" :key="idx">
            <img :src="url" alt="" />
          </div>
        </div>
        <div class="banner__dots" v-if="bannerList.length > 1">
          <span
            v-for="(_, idx) in bannerList"
            :key="idx"
            class="banner__dot"
            :class="{ 'is-active': idx === bannerIndex }"
          />
        </div>
      </section>

      <!-- 产品摘要 -->
      <header class="hero">
        <div class="hero__provider" v-if="detail.providerName">{{ detail.providerName }}</div>
        <h1 class="hero__title">{{ detail.productName }}</h1>
        <p class="hero__desc" v-if="detail.productSpecific">{{ detail.productSpecific }}</p>
        <p class="hero__premium" v-if="premiumText">
          <span class="hero__premium-label">参考保费</span>
          <span class="hero__premium-value">{{ premiumText }}</span>
        </p>
        <p class="hero__notice">{{ detail.noticeText }}</p>
        <p v-if="detail.salesUserName" class="hero__sales">顾问：{{ detail.salesUserName }}</p>
      </header>

      <!-- 产品特色 -->
      <section class="card" v-if="specifics.length > 0">
        <h2 class="card__title">产品特色</h2>
        <ul class="specific-list">
          <li v-for="(item, idx) in specifics" :key="idx" class="specific-item">
            <span class="specific-item__idx">{{ idx + 1 }}</span>
            <span class="specific-item__text">{{ item.content }}</span>
          </li>
        </ul>
      </section>

      <!-- 保障计划 -->
      <section class="card" v-if="plans.length > 0">
        <h2 class="card__title">保障计划</h2>
        <div class="plan-list">
          <div v-for="(plan, idx) in plans" :key="idx" class="plan-row">
            <div class="plan-row__name">{{ plan.name || '保障项' }}</div>
            <div class="plan-row__value" v-html="sanitizeHtml(plan.value || '')" />
          </div>
        </div>
      </section>

      <!-- 详情 Tabs：介绍 / 须知 / 责任 / 条款 -->
      <section class="card card--tabs" v-if="contentTabs.length > 0">
        <div class="tabs">
          <button
            v-for="tab in contentTabs"
            :key="tab.key"
            type="button"
            class="tabs__item"
            :class="{ 'is-active': activeTab === tab.key }"
            @click="activeTab = tab.key"
          >
            {{ tab.label }}
          </button>
        </div>
        <div class="tab-panel" v-if="activeTab === 'info' && detail.productInfoHtml">
          <div class="rich-html" v-html="sanitizeHtml(detail.productInfoHtml)" />
        </div>
        <div class="tab-panel" v-else-if="activeTab === 'notice' && detail.noticeHtml">
          <div class="rich-html" v-html="sanitizeHtml(detail.noticeHtml)" />
        </div>
        <div class="tab-panel" v-else-if="activeTab === 'liability' && detail.liabilityHtml">
          <div class="rich-html" v-html="sanitizeHtml(detail.liabilityHtml)" />
        </div>
        <div class="tab-panel" v-else-if="activeTab === 'clauses' && clauses.length > 0">
          <ul class="clause-list">
            <li v-for="(c, idx) in clauses" :key="idx" class="clause-item">
              <span class="clause-item__name">{{ c.name || '保险条款' }}</span>
              <a
                v-if="c.url"
                class="clause-item__link"
                :href="safeLink(c.url)"
                target="_blank"
                rel="noopener noreferrer"
              >
                查看
              </a>
            </li>
          </ul>
        </div>
      </section>

      <!-- 投保人表单 -->
      <section class="card" id="apply-form">
        <h2 class="card__title">投保人信息</h2>
        <form class="form" @submit.prevent="onSubmit">
          <label class="field">
            <span class="field__label">{{ isPersonalId ? '姓名' : '企业名称' }} <em>*</em></span>
            <input
              v-model.trim="form.customerName"
              :placeholder="isPersonalId ? '请输入投保人姓名' : '请输入企业全称'"
              maxlength="100"
            />
          </label>
          <label class="field">
            <span class="field__label">证件类型 <em>*</em></span>
            <select v-model="form.customerIdType">
              <option value="9">统一社会信用代码/营业执照</option>
              <option value="1">个人身份证</option>
              <option value="2">组织机构代码</option>
            </select>
          </label>
          <label class="field">
            <span class="field__label">证件号码 <em>*</em></span>
            <input
              v-model.trim="form.customerIdNo"
              placeholder="请输入证件号码"
              maxlength="32"
            />
          </label>
          <label class="field">
            <span class="field__label">所在地区 <em>*</em></span>
            <input v-model.trim="form.areaName" placeholder="如：北京市东城区" maxlength="64" />
          </label>
          <label class="field">
            <span class="field__label">联系人 <em>*</em></span>
            <input v-model.trim="form.customerContact" placeholder="联系人姓名" maxlength="32" />
          </label>
          <label class="field">
            <span class="field__label">手机号 <em>*</em></span>
            <input
              v-model.trim="form.customerMobile"
              type="tel"
              inputmode="numeric"
              placeholder="11 位手机号"
              maxlength="11"
            />
          </label>
          <label class="field">
            <span class="field__label">邮箱</span>
            <input v-model.trim="form.customerEmail" type="email" placeholder="选填" maxlength="64" />
          </label>
          <label class="field">
            <span class="field__label">详细地址</span>
            <input v-model.trim="form.customerAddress" placeholder="选填" maxlength="120" />
          </label>
          <div class="form-group">
            <div class="form-group__title">清单或图片</div>
            <label class="upload-line">
              <input
                type="file"
                accept="image/*,.pdf,.xls,.xlsx,.doc,.docx"
                @change="onFileChange('attachmentUrl', $event)"
              />
              <span class="upload-line__main">
                {{ uploadingField === 'attachmentUrl' ? '上传中…' : (form.attachmentName || '上传清单或图片') }}
              </span>
              <span class="upload-line__sub">选填，支持图片、PDF、Excel、Word，10MB以内</span>
            </label>
            <div v-if="form.attachmentUrl" class="file-actions">
              <button type="button" @click="removeFile('attachmentUrl')">移除附件</button>
            </div>
          </div>
          <label class="field">
            <span class="field__label">意向说明</span>
            <textarea v-model.trim="form.remark" placeholder="选填，如保障需求" maxlength="200" />
          </label>
          <p v-if="formError" class="form__error">{{ formError }}</p>
          <div class="footer-space" />
          <div class="footer-bar">
            <button class="btn-primary" type="submit" :disabled="submitting || !!uploadingField">
              {{ uploadingField ? '附件上传中…' : (submitting ? '提交中…' : '提交预投保') }}
            </button>
          </div>
        </form>
      </section>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, reactive, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import {
  getShareDetail,
  submitPreApply,
  uploadPreApplyFile,
  type ClauseItem,
  type PlanItem,
  type ShareDetail,
  type SpecificItem
} from '@/api/share'

const route = useRoute()
const shareCode = String(route.params.shareCode || '')

const loading = ref(true)
const submitting = ref(false)
const uploadingField = ref('')
const submitted = ref(false)
const error = ref('')
const formError = ref('')
const successMsg = ref('已登记预投保信息')
const detail = ref<ShareDetail | null>(null)

const bannerIndex = ref(0)
const activeTab = ref('info')
let bannerTimer: number | undefined
let touchStartX = 0

const form = reactive({
  customerName: '',
  customerIdType: '9',
  customerIdNo: '',
  areaId: 110101,
  areaName: '',
  customerContact: '',
  customerMobile: '',
  customerEmail: '',
  customerAddress: '',
  attachmentUrl: '',
  attachmentName: '',
  remark: ''
})

const isPersonalId = computed(() => form.customerIdType === '1')

const fixImageUrl = (url?: string) => {
  if (!url) return ''
  if (url.startsWith('//')) return `https:${url}`
  return url
}

const bannerList = computed(() => {
  const list = (detail.value?.bannerUrls || [])
    .map(fixImageUrl)
    .filter(Boolean)
  if (list.length > 0) return list
  const pic = fixImageUrl(detail.value?.productPicUrl)
  return pic ? [pic] : []
})

const specifics = computed<SpecificItem[]>(() =>
  (detail.value?.specifics || []).filter((s) => !!s?.content)
)

const plans = computed<PlanItem[]>(() =>
  (detail.value?.insurancePlans || []).filter((p) => !!(p?.name || p?.value))
)

const clauses = computed<ClauseItem[]>(() =>
  (detail.value?.clauses || []).filter((c) => !!(c?.name || c?.url))
)

const contentTabs = computed(() => {
  const tabs: { key: string; label: string }[] = []
  if (detail.value?.productInfoHtml) tabs.push({ key: 'info', label: '产品介绍' })
  if (detail.value?.noticeHtml) tabs.push({ key: 'notice', label: '投保须知' })
  if (detail.value?.liabilityHtml) tabs.push({ key: 'liability', label: '保障责任' })
  if (clauses.value.length > 0) tabs.push({ key: 'clauses', label: '保险条款' })
  return tabs
})

const premiumText = computed(() => {
  const start = detail.value?.premiumStart
  const end = detail.value?.premiumEnd
  const fmt = (v: number | string | undefined) => {
    if (v === undefined || v === null || v === '') return ''
    const n = Number(v)
    if (Number.isNaN(n)) return String(v)
    return n.toLocaleString('zh-CN', { maximumFractionDigits: 2 })
  }
  const s = fmt(start)
  const e = fmt(end)
  if (s && e) return s === e ? `¥${s} 起` : `¥${s} ~ ¥${e}`
  if (s) return `¥${s} 起`
  if (e) return `最高 ¥${e}`
  return ''
})

/** 轻量清理：去 script，修协议相对图链 */
const sanitizeHtml = (html: string) => {
  if (!html) return ''
  return html
    .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, '')
    .replace(/on\w+="[^"]*"/gi, '')
    .replace(/on\w+='[^']*'/gi, '')
    .replace(/src="\/\//g, 'src="https://')
    .replace(/href="\/\//g, 'href="https://')
}

const safeLink = (url?: string) => {
  if (!url) return '#'
  if (url.startsWith('//')) return `https:${url}`
  if (/^https?:\/\//i.test(url)) return url
  return '#'
}

const stopBannerAuto = () => {
  if (bannerTimer) {
    window.clearInterval(bannerTimer)
    bannerTimer = undefined
  }
}

const startBannerAuto = () => {
  stopBannerAuto()
  if (bannerList.value.length <= 1) return
  bannerTimer = window.setInterval(() => {
    bannerIndex.value = (bannerIndex.value + 1) % bannerList.value.length
  }, 4000)
}

const onTouchStart = (e: TouchEvent) => {
  touchStartX = e.changedTouches[0]?.clientX || 0
}

const onTouchEnd = (e: TouchEvent) => {
  const endX = e.changedTouches[0]?.clientX || 0
  const delta = endX - touchStartX
  if (Math.abs(delta) < 40 || bannerList.value.length <= 1) return
  if (delta < 0) {
    bannerIndex.value = (bannerIndex.value + 1) % bannerList.value.length
  } else {
    bannerIndex.value = (bannerIndex.value - 1 + bannerList.value.length) % bannerList.value.length
  }
  startBannerAuto()
}

watch(contentTabs, (tabs) => {
  if (tabs.length === 0) return
  if (!tabs.some((t) => t.key === activeTab.value)) {
    activeTab.value = tabs[0].key
  }
})

watch(bannerList, () => {
  bannerIndex.value = 0
  startBannerAuto()
})

watch(() => form.customerIdType, () => {
  formError.value = ''
})

const load = async () => {
  loading.value = true
  error.value = ''
  try {
    detail.value = await getShareDetail(shareCode)
    if (contentTabs.value.length > 0) {
      activeTab.value = contentTabs.value[0].key
    }
  } catch (e: any) {
    error.value = e?.message || '分享链接无效或已失效'
  } finally {
    loading.value = false
  }
}

const validate = () => {
  if (!form.customerName) return isPersonalId.value ? '请填写姓名' : '请填写企业名称'
  if (!form.customerIdNo) return '请填写证件号码'
  if (!form.areaName) return '请填写所在地区'
  if (!form.customerContact) return '请填写联系人'
  if (!/^1\d{10}$/.test(form.customerMobile)) return '请填写正确手机号'
  return ''
}

const uploadLabels: Record<string, string> = {
  attachmentUrl: '清单或图片'
}

const onFileChange = async (field: 'attachmentUrl', e: Event) => {
  const input = e.target as HTMLInputElement
  const file = input.files?.[0]
  if (!file) return
  if (file.size > 10 * 1024 * 1024) {
    formError.value = '单个附件不能超过 10MB'
    input.value = ''
    return
  }
  uploadingField.value = field
  formError.value = ''
  try {
    const url = await uploadPreApplyFile(file)
    form[field] = url
    form.attachmentName = file.name
  } catch (e: any) {
    formError.value = e?.message || `${uploadLabels[field]}上传失败，请重试`
  } finally {
    uploadingField.value = ''
    input.value = ''
  }
}

const removeFile = (field: 'attachmentUrl') => {
  form[field] = ''
  form.attachmentName = ''
}

const onSubmit = async () => {
  formError.value = validate()
  if (formError.value) return
  if (uploadingField.value) {
    formError.value = '附件正在上传，请稍后提交'
    return
  }
  submitting.value = true
  try {
    const res = await submitPreApply(shareCode, {
      customerName: form.customerName,
      customerIdType: form.customerIdType,
      customerIdNo: form.customerIdNo,
      areaId: form.areaId,
      areaName: form.areaName,
      customerContact: form.customerContact,
      customerMobile: form.customerMobile,
      customerEmail: form.customerEmail || undefined,
      customerAddress: form.customerAddress || undefined,
      attachmentUrl: form.attachmentUrl || undefined,
      attachmentName: form.attachmentName || undefined,
      remark: form.remark || undefined
    })
    successMsg.value =
      res.message || (res.duplicated ? '您已提交过，业务人员将尽快联系您' : '提交成功')
    submitted.value = true
    window.scrollTo({ top: 0, behavior: 'smooth' })
  } catch (e: any) {
    formError.value = e?.message || '提交失败，请稍后重试'
  } finally {
    submitting.value = false
  }
}

onMounted(() => {
  load()
  startBannerAuto()
})

onUnmounted(stopBannerAuto)
</script>

<style scoped>
.page {
  min-height: 100vh;
  padding-bottom: calc(88px + var(--safe-bottom));
}

.state {
  padding: 80px 24px;
  text-align: center;
  color: var(--muted);
}

.state--error {
  color: var(--danger);
}

/* 轮播 */
.banner {
  position: relative;
  overflow: hidden;
  background: #dfe9f5;
  height: 200px;
}

.banner__track {
  display: flex;
  height: 100%;
  transition: transform 0.35s ease;
  will-change: transform;
}

.banner__slide {
  flex: 0 0 100%;
  height: 100%;
}

.banner__slide img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.banner__dots {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 10px;
  display: flex;
  justify-content: center;
  gap: 6px;
}

.banner__dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.55);
}

.banner__dot.is-active {
  width: 14px;
  border-radius: 3px;
  background: #fff;
}

/* 产品摘要 */
.hero {
  background: linear-gradient(180deg, #e8f3ff 0%, var(--bg) 100%);
  padding: 16px 16px 8px;
}

.hero__provider {
  display: inline-block;
  font-size: 12px;
  color: var(--primary);
  background: rgba(22, 119, 255, 0.1);
  padding: 2px 8px;
  border-radius: 999px;
  margin-bottom: 8px;
}

.hero__title {
  margin: 0;
  font-size: 22px;
  font-weight: 700;
  line-height: 1.35;
}

.hero__desc {
  margin: 8px 0 0;
  color: #4e5969;
  font-size: 14px;
}

.hero__premium {
  margin: 10px 0 0;
  display: flex;
  align-items: baseline;
  gap: 8px;
}

.hero__premium-label {
  font-size: 12px;
  color: var(--muted);
}

.hero__premium-value {
  font-size: 18px;
  font-weight: 700;
  color: #f53f3f;
}

.hero__notice {
  margin: 10px 0 0;
  padding: 10px 12px;
  background: #fff7e8;
  color: #ad6800;
  border-radius: 10px;
  font-size: 13px;
}

.hero__sales {
  margin: 8px 0 0;
  font-size: 13px;
  color: var(--muted);
}

/* 通用卡片 */
.card {
  margin: 12px 16px 0;
  background: var(--card);
  border-radius: 16px;
  padding: 16px 14px 14px;
  box-shadow: 0 4px 16px rgba(15, 23, 42, 0.04);
}

.card--tabs {
  padding-bottom: 8px;
}

.card__title {
  margin: 0 0 12px;
  font-size: 16px;
  font-weight: 600;
}

/* 特色 */
.specific-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.specific-item {
  display: flex;
  gap: 10px;
  align-items: flex-start;
}

.specific-item__idx {
  flex: 0 0 22px;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: rgba(22, 119, 255, 0.12);
  color: var(--primary);
  font-size: 12px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 1px;
}

.specific-item__text {
  flex: 1;
  font-size: 14px;
  color: #4e5969;
  line-height: 1.5;
}

/* 保障计划 */
.plan-list {
  display: flex;
  flex-direction: column;
  border: 1px solid var(--border);
  border-radius: 12px;
  overflow: hidden;
}

.plan-row {
  display: grid;
  grid-template-columns: 96px 1fr;
  gap: 8px;
  padding: 12px;
  border-bottom: 1px solid var(--border);
  font-size: 13px;
}

.plan-row:last-child {
  border-bottom: none;
}

.plan-row__name {
  color: var(--muted);
  font-weight: 500;
}

.plan-row__value {
  color: var(--text);
  word-break: break-word;
}

.plan-row__value :deep(p) {
  margin: 0;
}

/* Tabs */
.tabs {
  display: flex;
  gap: 4px;
  overflow-x: auto;
  margin: -4px -4px 12px;
  padding: 4px;
  -webkit-overflow-scrolling: touch;
}

.tabs__item {
  flex: 0 0 auto;
  padding: 8px 12px;
  border-radius: 999px;
  background: transparent;
  color: #4e5969;
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
}

.tabs__item.is-active {
  background: rgba(22, 119, 255, 0.12);
  color: var(--primary);
}

.tab-panel {
  min-height: 40px;
}

.rich-html {
  font-size: 14px;
  color: #4e5969;
  line-height: 1.7;
  overflow-x: auto;
  word-break: break-word;
}

.rich-html :deep(img) {
  max-width: 100%;
  height: auto;
  border-radius: 8px;
}

.rich-html :deep(table) {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}

.rich-html :deep(td),
.rich-html :deep(th) {
  border: 1px solid var(--border);
  padding: 6px 8px;
}

.rich-html :deep(p) {
  margin: 0 0 8px;
}

.clause-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.clause-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid var(--border);
  font-size: 14px;
}

.clause-item:last-child {
  border-bottom: none;
}

.clause-item__name {
  flex: 1;
  color: var(--text);
}

.clause-item__link {
  color: var(--primary);
  text-decoration: none;
  font-weight: 500;
  flex: 0 0 auto;
}

/* 表单 */
.form {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.field__label {
  font-size: 13px;
  color: #4e5969;
}

.field__label em {
  color: var(--danger);
  font-style: normal;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 12px;
  border: 1px solid var(--border);
  border-radius: 12px;
  background: #fbfcff;
}

.form-group__title {
  font-size: 14px;
  font-weight: 600;
  color: var(--text);
}

.upload-line {
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 84px;
  padding: 12px;
  border: 1px dashed #b7c7dd;
  border-radius: 12px;
  background: #fff;
  color: var(--primary);
  overflow: hidden;
}

.upload-line input {
  position: absolute;
  inset: 0;
  opacity: 0;
}

.upload-line__main {
  margin-top: 6px;
  font-size: 13px;
  color: var(--primary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.upload-line__sub {
  margin-top: 4px;
  font-size: 12px;
  color: var(--muted);
}

.file-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.file-actions button {
  height: 30px;
  padding: 0 10px;
  border-radius: 15px;
  background: rgba(245, 63, 63, 0.08);
  color: var(--danger);
  font-size: 12px;
}

.form__error {
  margin: 0;
  color: var(--danger);
  font-size: 13px;
}

.footer-space {
  height: 8px;
}

.footer-bar {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 10px 16px calc(10px + var(--safe-bottom));
  background: rgba(255, 255, 255, 0.96);
  border-top: 1px solid var(--border);
  backdrop-filter: blur(8px);
  z-index: 20;
}

.btn-primary {
  width: 100%;
  height: 48px;
  border-radius: 24px;
  background: var(--primary);
  color: #fff;
  font-size: 16px;
  font-weight: 600;
}

.btn-primary:disabled {
  opacity: 0.65;
}

.btn-primary:active:not(:disabled) {
  background: var(--primary-pressed);
}

.success-card {
  margin: 48px 20px;
  padding: 36px 20px;
  background: var(--card);
  border-radius: 16px;
  text-align: center;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.06);
}

.success-card__icon {
  width: 56px;
  height: 56px;
  margin: 0 auto 16px;
  border-radius: 50%;
  background: #e8ffea;
  color: #00b42a;
  font-size: 28px;
  line-height: 56px;
  font-weight: 700;
}

.success-card h1 {
  margin: 0;
  font-size: 20px;
}

.success-card p {
  margin: 10px 0 0;
  color: #4e5969;
}

.success-card__sub {
  font-size: 13px !important;
  color: var(--muted) !important;
}
</style>
